import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Users, Target, AlertCircle, CheckCircle, X, Shield, UserPlus } from 'lucide-react';
import { doc, getDoc, updateDoc, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';

const TeamAssignmentPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const fetchLeague = async () => {
      try {
        const leagueDoc = await getDoc(doc(db, 'leagues', leagueId));
        if (leagueDoc.exists()) {
          setLeague({ id: leagueDoc.id, ...leagueDoc.data() });
        } else {
          setError('League not found');
        }
      } catch (err) {
        setError('Failed to load league details');
        console.error('Error fetching league:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeague();
  }, [leagueId]);

  useEffect(() => {
    let unsubscribeTeams = null;
    let unsubscribePlayers = null;

    const setupListeners = () => {
      const teamsQuery = query(
        collection(db, 'teams'),
        orderBy('name', 'asc')
      );

      unsubscribeTeams = onSnapshot(teamsQuery, (snapshot) => {
        const teamsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTeams(teamsData);
      });

      const playersQuery = query(
        collection(db, 'players'),
        orderBy('name', 'asc')
      );

      unsubscribePlayers = onSnapshot(playersQuery, (snapshot) => {
        const playersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPlayers(playersData);
      });
    };

    setupListeners();

    return () => {
      if (unsubscribeTeams) unsubscribeTeams();
      if (unsubscribePlayers) unsubscribePlayers();
    };
  }, []);

  const validateTeamConstraints = (team) => {
    const errors = [];
    const constraints = league?.teamConstraints;

    if (!constraints) return errors;

    const under18Count = team.players?.filter(p => p.key === 'Under 18').length || 0;
    const over40Count = team.players?.filter(p => p.key === 'Over 40').length || 0;
    const totalPlayers = team.players?.length || 0;

    if (under18Count < constraints.minUnder18) {
      errors.push(`${team.name}: Needs at least ${constraints.minUnder18} Under 18 players`);
    }
    if (under18Count > constraints.maxUnder18) {
      errors.push(`${team.name}: Cannot have more than ${constraints.maxUnder18} Under 18 players`);
    }
    if (over40Count < constraints.minOver40) {
      errors.push(`${team.name}: Needs at least ${constraints.minOver40} Over 40 players`);
    }
    if (over40Count > constraints.maxOver40) {
      errors.push(`${team.name}: Cannot have more than ${constraints.maxOver40} Over 40 players`);
    }
    if (totalPlayers > constraints.totalPlayers) {
      errors.push(`${team.name}: Cannot have more than ${constraints.totalPlayers} total players`);
    }

    return errors;
  };

  const validateAllTeams = () => {
    const allErrors = [];
    teams.forEach(team => {
      const teamErrors = validateTeamConstraints(team);
      allErrors.push(...teamErrors);
    });
    setValidationErrors(allErrors);
    return allErrors.length === 0;
  };

  const assignPlayerToTeam = async (playerId, teamId) => {
    try {
      await updateDoc(doc(db, 'players', playerId), {
        teamId: teamId,
        teamName: teams.find(t => t.id === teamId)?.name,
        updatedAt: new Date().toISOString()
      });

      const team = teams.find(t => t.id === teamId);
      const player = players.find(p => p.id === playerId);
      
      if (team && player) {
        const updatedPlayers = [...(team.players || []), {
          id: player.id,
          name: player.name,
          category: player.category,
          key: player.key,
          selected: true
        }];

        await updateDoc(doc(db, 'teams', teamId), {
          players: updatedPlayers,
          playerIds: updatedPlayers.map(p => p.id),
          updatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error assigning player:', error);
    }
  };

  const removePlayerFromTeam = async (playerId, teamId) => {
    try {
      await updateDoc(doc(db, 'players', playerId), {
        teamId: null,
        teamName: null,
        updatedAt: new Date().toISOString()
      });

      const team = teams.find(t => t.id === teamId);
      
      if (team) {
        const updatedPlayers = (team.players || []).filter(p => p.id !== playerId);

        await updateDoc(doc(db, 'teams', teamId), {
          players: updatedPlayers,
          playerIds: updatedPlayers.map(p => p.id),
          updatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error removing player:', error);
    }
  };

  const handleSave = async () => {
    if (!validateAllTeams()) {
      setError('Please fix all validation errors before saving');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await updateDoc(doc(db, 'leagues', leagueId), {
        status: 'ready',
        teamsAssigned: true,
        updatedAt: new Date().toISOString()
      });

      navigate('/league');
    } catch (error) {
      setError('Failed to save team assignments');
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Shield size={48} style={{ margin: '0 auto 1rem', color: '#10B981' }} />
          </motion.div>
          <p style={{ color: '#6B7280', fontWeight: '600' }}>Loading league details...</p>
        </div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div style={{ textAlign: 'center', padding: '3rem 0' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1C563D' }}>
            {error}
          </h2>
          <button
            onClick={() => navigate('/league')}
            className="btn btn-primary"
          >
            Back to League
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F0FDF4 0%, #FFFFFF 50%, #DBEAFE 100%)' }}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '2rem' }}
        >
          <button
            onClick={() => navigate('/league')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#6B7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}
          >
            <ArrowLeft size={20} />
            Back to League
          </button>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1C563D' }}>
            Team Assignment - {league?.name}
          </h1>
          <p style={{ color: '#6B7280', fontSize: '1.125rem' }}>
            Assign players to teams and validate team constraints
          </p>
        </motion.div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '1.5rem' }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
              border: '2px solid #FCA5A5',
              borderRadius: '0.75rem',
              padding: '1rem',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <AlertCircle size={20} style={{ color: '#DC2626', marginRight: '0.5rem' }} />
                <h3 style={{ fontWeight: '700', color: '#991B1B', fontSize: '1rem' }}>Validation Errors</h3>
              </div>
              <ul style={{ fontSize: '0.875rem', color: '#B91C1C', marginLeft: '1.5rem' }}>
                {validationErrors.map((error, index) => (
                  <li key={index} style={{ marginBottom: '0.25rem' }}>• {error}</li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}

        {/* Team Constraints Info */}
        {league?.teamConstraints && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              marginBottom: '1.5rem'
            }}
          >
            <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem', color: '#1C563D' }}>
              Team Constraints
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', fontSize: '0.875rem' }}>
              <div style={{ padding: '0.75rem', background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)', borderRadius: '0.5rem' }}>
                <span style={{ fontWeight: '700', color: '#065F46' }}>Under 18:</span>
                <p style={{ color: '#374151', marginTop: '0.25rem' }}>
                  {league.teamConstraints.minUnder18} - {league.teamConstraints.maxUnder18} players
                </p>
              </div>
              <div style={{ padding: '0.75rem', background: 'linear-gradient(135deg, #FEF3C7 0%, #FCD34D 100%)', borderRadius: '0.5rem' }}>
                <span style={{ fontWeight: '700', color: '#92400E' }}>Over 40:</span>
                <p style={{ color: '#374151', marginTop: '0.25rem' }}>
                  {league.teamConstraints.minOver40} - {league.teamConstraints.maxOver40} players
                </p>
              </div>
              <div style={{ padding: '0.75rem', background: 'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)', borderRadius: '0.5rem' }}>
                <span style={{ fontWeight: '700', color: '#1E40AF' }}>Total:</span>
                <p style={{ color: '#374151', marginTop: '0.25rem' }}>
                  Max {league.teamConstraints.totalPlayers} players
                </p>
              </div>
              <div style={{ padding: '0.75rem', background: validationErrors.length === 0 ? 'linear-gradient(135deg, #D1FAE5 0%, #6EE7B7 100%)' : 'linear-gradient(135deg, #FEE2E2 0%, #FCA5A5 100%)', borderRadius: '0.5rem' }}>
                <span style={{ fontWeight: '700', color: validationErrors.length === 0 ? '#065F46' : '#991B1B' }}>Status:</span>
                <p style={{ color: '#374151', marginTop: '0.25rem', fontWeight: '600' }}>
                  {validationErrors.length === 0 ? '✓ Valid' : '✗ Invalid'}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Content */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          {/* Teams Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: '#1C563D', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Shield size={24} />
              Teams ({teams.length})
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              {teams.map((team, index) => {
                const teamErrors = validateTeamConstraints(team);
                const under18Count = team.players?.filter(p => p.key === 'Under 18').length || 0;
                const over40Count = team.players?.filter(p => p.key === 'Over 40').length || 0;
                
                return (
                  <motion.div
                    key={team.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    style={{
                      padding: '1.25rem',
                      border: teamErrors.length > 0 ? '2px solid #FCA5A5' : '2px solid #6EE7B7',
                      borderRadius: '0.75rem',
                      background: teamErrors.length > 0 ? 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)' : 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {team.logo ? (
                          <img src={team.logo} alt={team.name} style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }} />
                        ) : (
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Shield size={24} style={{ color: 'white' }} />
                          </div>
                        )}
                        <h3 style={{ fontWeight: '700', fontSize: '1.125rem', color: '#1F2937' }}>{team.name}</h3>
                      </div>
                      {teamErrors.length === 0 ? (
                        <CheckCircle size={20} style={{ color: '#10B981' }} />
                      ) : (
                        <X size={20} style={{ color: '#EF4444' }} />
                      )}
                    </div>
                    
                    <div style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.75rem', fontWeight: '600' }}>
                      Players: {team.players?.length || 0} | Under 18: {under18Count} | Over 40: {over40Count}
                    </div>
                    
                    {team.players && team.players.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto', padding: '0.5rem', background: 'white', borderRadius: '0.5rem' }}>
                        {team.players.map(player => (
                          <div key={player.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.875rem', padding: '0.5rem', borderRadius: '0.25rem', background: 'rgba(28, 86, 61, 0.05)' }}>
                            <span style={{ fontWeight: '600', color: '#374151' }}>
                              {player.name} <span style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>({player.category})</span>
                            </span>
                            <button
                              onClick={() => removePlayerFromTeam(player.id, team.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0.25rem',
                                borderRadius: '0.25rem',
                                transition: 'all 0.2s'
                              }}
                            >
                              <X size={16} style={{ color: '#EF4444' }} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Available Players */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: '#1C563D', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <UserPlus size={24} />
              Available Players ({players.filter(p => !p.teamId).length})
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0.75rem' }}>
              {players
                .filter(player => !player.teamId)
                .map((player, index) => (
                  <motion.div
                    key={player.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    whileHover={{ scale: 1.01 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      border: '1px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      background: 'white',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '0.875rem' }}>{player.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                        {player.category} • {player.key || 'No key'} • {player.position}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {teams.map(team => (
                        <button
                          key={team.id}
                          onClick={() => assignPlayerToTeam(player.id, team.id)}
                          className="btn btn-sm btn-secondary"
                          style={{
                            padding: '0.375rem 0.75rem',
                            fontSize: '0.75rem',
                            minHeight: 'auto',
                            minWidth: '36px'
                          }}
                          title={`Assign to ${team.name}`}
                        >
                          {team.name.charAt(0)}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ))}
            </div>
          </motion.div>
        </div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}
        >
          <button
            onClick={handleSave}
            disabled={saving || validationErrors.length > 0}
            className="btn btn-primary"
            style={{
              background: (saving || validationErrors.length > 0) ? '#9CA3AF' : 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
              cursor: (saving || validationErrors.length > 0) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Team Assignments'}
          </button>
        </motion.div>

        {error && (
          <div style={{
            background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
            border: '2px solid #FCA5A5',
            color: '#991B1B',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginTop: '1rem'
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamAssignmentPage;
